import { Edit, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import styled from 'styled-components';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Input, InputGroup } from '../components/common/Input';
import Footer from '../components/layout/Footer';
import Sidebar from '../components/layout/Sidebar';
import { useMenu } from '../context/MenuContext';
import { theme } from '../styles/theme';

const Container = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${theme.colors.background};
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: 260px;
  padding: ${theme.spacing.xl};
  padding-bottom: 80px;

  @media (max-width: ${theme.breakpoints.lg}) {
    margin-left: 0;
    padding: ${theme.spacing.md};
    padding-top: 70px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.sm};
    padding-top: 70px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.xl};
  flex-wrap: wrap;
  gap: ${theme.spacing.md};
  
  h1 {
    font-size: ${theme.typography.fontSize['3xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.text};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    h1 {
      font-size: ${theme.typography.fontSize['2xl']};
    }
  }
`;

const ActionsBar = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  padding: ${theme.spacing.lg};
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.md};
`;

const MenuGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.md};
  }
`;

const MenuItemCard = styled(Card)`
  padding: ${theme.spacing.lg};
  position: relative;
  display: flex;
  flex-direction: column;
`;

const CategoryBadge = styled.span`
  position: absolute;
  top: ${theme.spacing.sm};
  right: ${theme.spacing.sm};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semiBold};
  background-color: ${theme.colors.primary};
  color: ${theme.colors.white};
`;

const MenuItemName = styled.h3`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  margin-bottom: ${theme.spacing.sm};
  color: ${theme.colors.text};
  padding-right: 80px;

  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.typography.fontSize.lg};
    padding-right: 60px;
  }
`;

const MenuItemDescription = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  margin-bottom: ${theme.spacing.md};
  flex: 1;
`;

const MenuItemPrice = styled.div`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.md};
`;

const AvailabilityStatus = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
  font-size: ${theme.typography.fontSize.sm};
  color: ${({ available }) => available ? theme.colors.status.ready : theme.colors.status.outOfStock};
`;

const MenuItemActions = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-top: auto;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${theme.zIndex.modal};
  padding: ${theme.spacing.lg};
`;

const ModalContent = styled(Card)`
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.border};

  h2 {
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.semiBold};
    color: ${theme.colors.text};
  }
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.md};
  border: none;
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.text};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.status.outOfStock};
    color: ${theme.colors.white};
  }
`;

const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  input[type="checkbox"] {
    width: 20px;
    height: 20px;
    cursor: pointer;
  }

  label {
    font-size: ${theme.typography.fontSize.base};
    cursor: pointer;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${theme.spacing.lg};
`;

const categories = ["Hot Drinks","Cold Drinks","Salads", "Rayata Special ","Breakfast","Sandwiches","South Indian","Chinese","Chinese Rice","Soup","Paneer Starters","Papad","Basmati Special","Marathi Special","Main Course/Veg Special","Main Course Paneer Special","Punjabi Dish","Tandoor Roti","Paratha","Rice Plate"];

function MenuManagement() {
  // Use shared menu from MenuContext (real-time updates across pages)
  const { menuItems, addMenuItem, updateMenuItem, deleteMenuItem, toggleAvailability, clearAllMenu, loading, error, fetchMenu } = useMenu();
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: 'Starters',
    available: true,
  });

  const handleAddItem = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      category: 'Starters',
      available: true,
    });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description,
      price: item.price,
      category: item.category,
      available: item.available,
    });
    setShowModal(true);
  };

  const handleDelete = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this menu item?')) {
      await deleteMenuItem(itemId);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingItem) {
      await updateMenuItem(editingItem.id, { ...formData, price: parseFloat(formData.price) });
    } else {
      await addMenuItem({ ...formData, price: parseFloat(formData.price) });
    }
    setShowModal(false);
    setFormData({
      name: '',
      description: '',
      price: '',
      category: 'Starters',
      available: true,
    });
  };

  const handleToggleAvailability = async (itemId) => {
    await toggleAvailability(itemId);
  };

  const handleClearAll = async () => {
    if (window.confirm('Are you sure you want to delete ALL menu items? This action cannot be undone.')) {
      await clearAllMenu();
    }
  };

  const filteredItems = menuItems.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container>
      <Sidebar />
      <MainContent>
        <Header>
          <h1>Menu Management</h1>
          <div style={{ display: 'flex', gap: theme.spacing.md }}>
            <Button variant="primary" onClick={handleAddItem}>
              <Plus size={20} />
              Add Menu Item
            </Button>
          </div>
        </Header>

        <ActionsBar>
          <Input
            type="text"
            placeholder="Search menu items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1, maxWidth: '400px' }}
          />
        </ActionsBar>

        {loading && (
          <Card style={{ textAlign: 'center', padding: theme.spacing['3xl'] }}>
            <p style={{ color: theme.colors.textSecondary }}>Loading menu items...</p>
          </Card>
        )}

        {error && (
          <Card style={{ textAlign: 'center', padding: theme.spacing['3xl'], border: '2px solid #dc3545' }}>
            <p style={{ color: '#dc3545', fontWeight: 'bold' }}>Error: {error}</p>
            <p style={{ color: theme.colors.textSecondary, fontSize: '14px', marginTop: '8px' }}>
              Check console (F12) for details. This may be a CORS issue when accessing the API from localhost.
            </p>
          </Card>
        )}

        {!loading && !error && (
        <MenuGrid>
          {filteredItems.length === 0 ? (
            <Card style={{ gridColumn: '1 / -1', textAlign: 'center', padding: theme.spacing['3xl'] }}>
              <p style={{ color: theme.colors.textSecondary }}>No menu items found</p>
            </Card>
          ) : (
            filteredItems.map((item) => (
              <MenuItemCard key={item.id}>
                <CategoryBadge>{item.category}</CategoryBadge>
                <MenuItemName>{item.name}</MenuItemName>
                <MenuItemDescription>{item.description}</MenuItemDescription>
                <MenuItemPrice>₹{item.price}</MenuItemPrice>
                <AvailabilityStatus available={item.available}>
                  {item.available ? '✓ Available' : '✗ Out of Stock'}
                </AvailabilityStatus>
                <MenuItemActions>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(item)}
                  >
                    <Edit size={16} />
                    Edit
                  </Button>
                  <Button
                    variant={item.available ? 'secondary' : 'primary'}
                    size="sm"
                    onClick={() => handleToggleAvailability(item.id)}
                  >
                    {item.available ? 'Disable' : 'Enable'}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Trash2 size={16} />
                    Delete
                  </Button>
                </MenuItemActions>
              </MenuItemCard>
            ))
          )}
        </MenuGrid>
        )}

        {showModal && (
          <Modal onClick={() => setShowModal(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <h2>{editingItem ? 'Edit Menu Item' : 'Add Menu Item'}</h2>
                <CloseButton onClick={() => setShowModal(false)}>×</CloseButton>
              </ModalHeader>
              <ModalForm onSubmit={handleSubmit}>
                <InputGroup>
                  <label>Item Name</label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </InputGroup>
                <InputGroup>
                  <label>Description</label>
                  <Input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                  />
                </InputGroup>
                <InputGroup>
                  <label>Price (₹)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                  />
                </InputGroup>
                <InputGroup>
                  <label>Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      fontSize: theme.typography.fontSize.base,
                      fontFamily: theme.typography.fontFamily,
                      border: `2px solid ${theme.colors.border}`,
                      borderRadius: theme.borderRadius.md,
                      backgroundColor: theme.colors.white,
                    }}
                    required
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </InputGroup>
                <CheckboxGroup>
                  <input
                    type="checkbox"
                    id="available"
                    checked={formData.available}
                    onChange={(e) => setFormData({ ...formData, available: e.target.checked })}
                  />
                  <label htmlFor="available">Available</label>
                </CheckboxGroup>
                <ButtonGroup>
                  <Button variant="secondary" type="button" onClick={() => setShowModal(false)}>
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit">
                    {editingItem ? 'Update' : 'Add'} Item
                  </Button>
                </ButtonGroup>
              </ModalForm>
            </ModalContent>
          </Modal>
        )}
      </MainContent>
      <Footer />
    </Container>
  );
}

export default MenuManagement;

